import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReceiptData } from "../../hooks/useReceiptData";
import logo from "../../logo.png";
import { getReceiptPdf, saveEmail } from "../../services/Receipt";
import { getFormattedLanguage, getLocale } from "../../utils/receipt";
import "../style.scss";
import useSnackbar from "../../hooks/useSnackbar";
import type { LogoImageProps } from "../../types/index.types";
import { ReceiptFound } from "./ReceiptFound";
import { ReceiptNotFound } from "./ReceiptNotFound";

export const LogoImage = ({ operatorLogo }: LogoImageProps) => (
	<img loading="lazy" src={operatorLogo || logo} className="logo" alt="logo" />
);

const Receipt = () => {
	const { receiptData, isLoading, transactionId, boxName } = useReceiptData();
	const [isPdfLoading, setIsPdfLoading] = useState(false);
	const [isEmailLoading, setIsEmailLoading] = useState(false);
	const refForm = useRef<HTMLFormElement>(null);
	const { t, i18n } = useTranslation();
	const locale = getLocale(i18n.language);
	const { showSnackbar, SnackbarComponent } = useSnackbar();

	useEffect(() => {
		if (receiptData?.language) {
			const lang = getFormattedLanguage(receiptData.language);
			i18n.changeLanguage(lang);
		}
	}, [i18n, receiptData?.language]);

	useEffect(()=>{
		document.title = t("receipt.title");
	}, [t])

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const formData = new FormData(event.currentTarget);
		const email = formData.get("email");
		if (!email) return;
		setIsEmailLoading(true);
		try {
			const success = await saveEmail(transactionId, boxName, email as string);
			setIsEmailLoading(false);
			if (!success) throw new Error(t("receipt.email_error"));
			refForm.current?.reset();
			showSnackbar(t("receipt.email_success"), "success");
		} catch (error) {
			setIsEmailLoading(false);
			showSnackbar(t("receipt.email_error"), "error");
		}
	};

	const downloadPdf = async () => {
		if (!transactionId || !boxName) return;
		setIsPdfLoading(true);
		try {
			const data = await getReceiptPdf(transactionId, boxName);
			setIsPdfLoading(false);
			if (!data?.pdfUrl) throw new Error(t("receipt.pdf_error"));
			window.open(data.pdfUrl, "_blank");
		} catch (error) {
			setIsPdfLoading(false);
			showSnackbar(t("receipt.pdf_error"), "error");
		}
	};

	if (isLoading) {
		return (
			<Container maxWidth="xs" className="receipt-main-container">
				<Card className="card-container">
					<div className="receipt-container">
						<div className="nav">
							<LogoImage />
							<h1>{t("receipt.title")}</h1>
						</div>
						<div className="header items-center">
							<CircularProgress />
						</div>
					</div>
				</Card>
			</Container>
		);
	}

	return (
		<Container maxWidth="xs" className="receipt-main-container">
			{receiptData?.error ? (
				<ReceiptNotFound
					data={receiptData}
					transactionId={transactionId}
					boxName={boxName}
					handleSubmit={handleSubmit}
					isEmailLoading={isEmailLoading}
					refForm={refForm}
				/>
			) : (
				<ReceiptFound
					data={receiptData}
					locale={locale}
					handleSubmit={handleSubmit}
					downloadPdf={downloadPdf}
					isPdfLoading={isPdfLoading}
					isEmailLoading={isEmailLoading}
					refForm={refForm}
				/>
			)}
			<SnackbarComponent />
		</Container>
	);
};

export default Receipt;
